<template>
  <div class="drawer-container">
    <div class="drawer-header">
      <div class="flex align-center justify-between mb-3">
        <div class="text-24 text-bold">
          AI 貿易書類照合結果
        </div>
        <div class="flex gap-2">
          <el-button @click="handleClose" type="default" icon="el-icon-close">
            前の画面に戻る
          </el-button>
        </div>
      </div>
      <el-alert
        type="success"
        effect="dark"
        :closable="false"
        class="custom-success-alert"
      >
        <div>
          選択された2つの書類を比較しています。
        </div>
      </el-alert>
    </div>

    <div class="drawer-body flex pt-2 pb-4">
      <div class="content-area">
        <!-- 選択されたドキュメント情報 -->
        <div class="selected-documents-info mb-4">
          <h3 class="text-18 text-bold mb-3">選択された書類</h3>
          <el-row :gutter="20">
            <el-col
              :span="12"
              v-for="(doc, index) in selectedDocuments"
              :key="index"
            >
              <el-collapse v-model="activeDocuments" class="document-collapse">
                <el-collapse-item :name="String(index)">
                  <!-- カスタムヘッダー -->
                  <template slot="title">
                    <div class="collapse-header">
                      <span class="document-title pl-2">
                        <span class="text-bold">{{ selectedDocuments[index] && selectedDocuments[index].documentCategory || '書類 ' + (index + 1) }}</span>
                        <span class="document-name ml-2">{{
                          doc.documentName
                        }}</span>
                      </span>
                      <span class="process-name ml-2">
                        {{ doc.processName }}
                      </span>
                    </div>
                  </template>
                  <!-- 詳細情報（展開時に表示） -->
                  <div class="collapse-body">
                    <div class="info-row">
                      <span class="label">ファイル名:</span>
                      <span class="value">{{ doc.documentName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">プロセス名:</span>
                      <span class="value">{{ doc.processName }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">日付:</span>
                      <span class="value">{{ doc.date }}</span>
                    </div>
                    <div class="info-row">
                      <span class="label">サイズ:</span>
                      <span class="value">{{ doc.size }}</span>
                    </div>

                    <!-- PDFプレビューエリア -->
                    <div class="pdf-preview-container">
                      <div v-if="pdfUrls[doc.documentManageId]" class="pdf-preview-wrapper">
                        <div class="pdf-preview-header">
                          <el-button
                            type="text"
                            size="small"
                            @click="closePdfPreview(doc.documentManageId)"
                            icon="el-icon-close"
                          >
                            プレビューを閉じる
                          </el-button>
                        </div>
                        <iframe
                          :src="pdfUrls[doc.documentManageId]"
                          class="pdf-preview-iframe"
                        ></iframe>
                      </div>
                      <div v-else-if="pdfLoading[doc.documentManageId]" class="pdf-loading">
                        <i class="el-icon-loading"></i> PDFを読み込み中...
                      </div>
                      <div v-else class="pdf-placeholder">
                        <el-button
                          type="primary"
                          size="small"
                          @click="loadPdfPreview(doc)"
                          icon="el-icon-document"
                        >
                          PDFをプレビュー
                        </el-button>
                        <el-button
                          type="default"
                          size="small"
                          @click="handleOpenPdfInNewWindow(doc)"
                          icon="el-icon-top-right"
                          :loading="pdfLoading[doc.documentManageId]"
                          class="ml-2"
                        >
                          別タブでプレビュー
                        </el-button>
                      </div>
                    </div>
                  </div>
                </el-collapse-item>
              </el-collapse>
            </el-col>
          </el-row>
        </div>

        <!-- 比較結果表示エリア -->
        <div class="comparison-result">
          <div class="comparison-result-header">
            <h3 class="text-18 text-bold">比較結果</h3>
          </div>

          <el-tabs v-model="activeTab" type="card">
            <!-- ヘッダー項目タブ -->
            <el-tab-pane label="ヘッダー項目" name="header">
              <!-- ヘッダー項目TSVダウンロードボタン -->
              <div class="mb-5">
                <el-button
                  type="normal"
                  size="small"
                  icon="el-icon-download"
                  @click="downloadHeaderTSV"
                >
                  TSVダウンロード
                </el-button>
              </div>

              <TwTable
                :tableData="comparisonData"
                :cols="cols"
                :loading="false"
                :offsetY="525"
              >
                <!-- 項目名のカスタム表示 -->
                <template v-slot:ocrFieldName="{ row }">
                  <span :class="{ 'text-bold text-error': !row.isMatch }">
                    {{ row.ocrFieldName }}
                  </span>
                </template>
                <!-- 書類1のカスタム表示 -->
                <template v-slot:doc1Value="{ row }">
                  <span :class="{ 'text-bold text-error': !row.isMatch }">
                    {{ row.doc1Value }}
                  </span>
                </template>
                <!-- 書類2のカスタム表示 -->
                <template v-slot:doc2Value="{ row }">
                  <span :class="{ 'text-bold text-error': !row.isMatch }">
                    {{ row.doc2Value }}
                  </span>
                </template>
                <!-- 判定のカスタム表示 -->
                <template v-slot:matchStatus="{ row }">
                  <el-tag
                    :type="row.isMatch ? 'success' : 'danger'"
                  >
                    {{ row.matchStatus }}
                  </el-tag>
                </template>
                <!-- 差分のカスタム表示 -->
                <template v-slot:differences="{ row }">
                  <div
                    v-if="row.differences"
                    style="white-space: pre-wrap; line-height: 1.6;"
                  >
                    {{ row.differences }}
                  </div>
                </template>
              </TwTable>
            </el-tab-pane>

            <!-- 明細項目タブ -->
            <el-tab-pane label="明細項目" name="detail">
              <CrossCheckItemsComparision
                :compareData="compareData"
                :selectedDocuments="selectedDocuments"
                @download-tsv="downloadDetailTSV"
              />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TwTable from '@/components/vue3/TwTable.vue';
import { usePdfPreview } from '@/composables/usePdfPreview';
import CrossCheckItemsComparision from './crossCheckItemsComparision.vue';

export default {
  name: 'CrossCheckAiOcrDetail',
  components: {
    TwTable,
    CrossCheckItemsComparision,
  },
  setup() {
    const { openPdfInNewWindow, downloadPdf } = usePdfPreview();

    return {
      openPdfInNewWindow,
      downloadPdf,
    };
  },
  props: {
    selectedDocuments: {
      type: Array,
      default: () => [],
    },
    compareData: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      comparisonData: [],
      activeTab: 'header',
      pdfLoading: {},
      pdfUrls: {}, // PDFプレビュー用URL保存
      activeDocuments: [], // 展開中のドキュメント（デフォルトは全て折りたたみ）
      // ヘッダー項目比較で表示する項目リスト
      displayHeaderFields: [
        'poNo',
        'contractNo',
        'buyerName',
        'buyerAddress1',
        'buyerAddress2',
        'buyerAddress3',
        'buyerAddress4',
        'consigneeName',
        'consigneeAddress1',
        'consigneeAddress2',
        'consigneeAddress3',
        'consigneeAddress4',
        'shipperName',
        'shipperAddress1',
        'shipperAddress2',
        'shipperAddress3',
        'shipperAddress4',
        'portOfLoadingName',
        'portOfDischargingName',
        'paymentTerms1',
        'deliveryTermsId',
        'deliveryTermsPlaceName',
        'exCustomsTotalQuantity', // 合計数量（結合表示）
        'detailRowCount', // 明細行数（仮想フィールド）
        'exCustomsTotalAmount', // 合計金額（通貨含む）
      ],
      // フィールド名の日本語表示マッピング
      displayFieldLabels: {
        poNo: 'P/O No.',
        contractNo: 'Contract No.',
        buyerName: 'Buyer Name',
        buyerAddress1: 'Buyer Address1',
        buyerAddress2: 'Buyer Address2',
        buyerAddress3: 'Buyer Address3',
        buyerAddress4: 'Buyer Address4',
        consigneeName: 'Consignee Name',
        consigneeAddress1: 'Consignee Address1',
        consigneeAddress2: 'Consignee Address2',
        consigneeAddress3: 'Consignee Address3',
        consigneeAddress4: 'Consignee Address4',
        shipperName: 'Shipper Name',
        shipperAddress1: 'Shipper Address1',
        shipperAddress2: 'Shipper Address2',
        shipperAddress3: 'Shipper Address3',
        shipperAddress4: 'Shipper Address4',
        portOfLoadingName: '（ETD）Port of Loading',
        portOfDischargingName: 'Port of Discharging',
        paymentTerms1: 'Payment Terms',
        deliveryTermsId: 'Delivery Terms (Incoterms)',
        deliveryTermsPlaceName: 'Delivery Terms (Place)',
        exCustomsTotalQuantity: '合計数量',
        detailRowCount: '明細行数',
        exCustomsTotalAmount: '合計金額',
      },
    };
  },
  computed: {
    doc1Label() {
      return this.selectedDocuments[0]?.documentCategory || '書類1';
    },
    doc2Label() {
      return this.selectedDocuments[1]?.documentCategory || '書類2';
    },
    cols() {
      return [
        {
          prop: 'ocrFieldName',
          label: '項目名',
          width: 250,
          align: 'left',
          sortable: false,
          showOverflowTooltip: true,
        },
        {
          prop: 'doc1Value',
          label: this.doc1Label,
          width: 200,
          align: 'left',
          sortable: false,
          showOverflowTooltip: true,
        },
        {
          prop: 'doc2Value',
          label: this.doc2Label,
          width: 200,
          align: 'left',
          sortable: false,
          showOverflowTooltip: true,
        },
        {
          prop: 'matchStatus',
          label: '判定',
          width: 100,
          align: 'center',
          sortable: false,
        },
        {
          prop: 'differences',
          label: '差分',
          width: 300,
          align: 'left',
          sortable: false,
        },
      ];
    },
  },
  watch: {
    // compareDataが変更されたらヘッダー項目比較を再実行
    compareData: {
      handler(newVal) {
        if (newVal && newVal.documents) {
          this.compareHeaderItems();
        }
      },
      deep: true,
      immediate: true, // 初回マウント時にも実行
    },
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    // PDFプレビューを読み込む
    async loadPdfPreview(doc) {
      // 既に読み込み済みの場合はスキップ
      if (this.pdfUrls[doc.documentManageId]) {
        return;
      }

      this.$set(this.pdfLoading, doc.documentManageId, true);

      try {
        const src = await this.downloadPdf(
          doc.documentManageId,
          doc.documentTrx,
          doc.documentName,
        );

        // PDF URLを保存
        this.$set(this.pdfUrls, doc.documentManageId, src);
      } catch (err) {
        console.error('PDFプレビュー読み込みエラー:', err);
        this.$message.error('PDFの取得に失敗しました');
      } finally {
        this.$set(this.pdfLoading, doc.documentManageId, false);
      }
    },
    // 別ウィンドウでPDFを開く
    async handleOpenPdfInNewWindow(doc) {
      try {
        await this.openPdfInNewWindow(
          doc.documentManageId,
          doc.documentTrx,
          doc.documentName,
        );
      } catch (err) {
        this.$message.error('PDFの取得に失敗しました');
      }
    },
    // PDFプレビューを閉じる
    closePdfPreview(documentManageId) {
      this.$delete(this.pdfUrls, documentManageId);
    },
    // TSVダウンロード
    downloadTSV() {
      if (this.activeTab === 'header') {
        this.downloadHeaderTSV();
      } else if (this.activeTab === 'detail') {
        this.downloadDetailTSV();
      }
    },
    // ヘッダー項目TSVダウンロード
    downloadHeaderTSV() {
      if (!this.comparisonData || this.comparisonData.length === 0) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      // TSVヘッダー行
      const headers = ['項目名', this.doc1Label, this.doc2Label, '判定', '差分'];

      // TSVデータ行
      const rows = this.comparisonData.map((row) => [
        row.ocrFieldName,
        row.doc1Value,
        row.doc2Value,
        row.matchStatus,
        row.differences ? row.differences.replace(/\n/g, ' ') : '',
      ]);

      this.generateTSVFile(headers, rows, 'ヘッダー項目比較結果');
    },
    // 明細項目TSVダウンロード
    downloadDetailTSV(filteredData) {
      // filteredData が渡された場合はフィルタ済みデータでダウンロード
      if (filteredData && Array.isArray(filteredData)) {
        this.downloadFilteredDetailTSV(filteredData);
        return;
      }

      // 既存の処理（フォールバック: 全データ）
      const doc1 = this.compareData?.documents?.[0];
      const doc2 = this.compareData?.documents?.[1];

      if (!doc1 || !doc2) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      const doc1Items =
        doc1.ocrResults?.aiocrTableItems?.goodsLine || [];
      const doc2Items =
        doc2.ocrResults?.aiocrTableItems?.goodsLine || [];
      const maxLength = Math.max(doc1Items.length, doc2Items.length);

      if (maxLength === 0) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      // TSVヘッダー行
      const headers = [
        '明細No',
        `${this.doc1Label}_商品名`,
        `${this.doc1Label}_商品番号`,
        `${this.doc1Label}_数量`,
        `${this.doc1Label}_単価`,
        `${this.doc1Label}_金額`,
        `${this.doc2Label}_商品名`,
        `${this.doc2Label}_商品番号`,
        `${this.doc2Label}_数量`,
        `${this.doc2Label}_単価`,
        `${this.doc2Label}_金額`,
        '判定',
        '差分',
      ];

      // TSVデータ行
      const rows = [];
      for (let i = 0; i < maxLength; i++) {
        const doc1Item = doc1Items[i] || {};
        const doc2Item = doc2Items[i] || {};
        const lineNo = i + 1;

        // 各項目の値を取得
        const doc1GoodsShortName = doc1Item.ocrGoodsShortName || '';
        const doc2GoodsShortName = doc2Item.ocrGoodsShortName || '';
        const doc1GoodsRefNo1 = doc1Item.ocrGoodsRefNo1 || '';
        const doc2GoodsRefNo1 = doc2Item.ocrGoodsRefNo1 || '';
        const doc1Quantity = this.formatQuantityForTSV(doc1Item);
        const doc2Quantity = this.formatQuantityForTSV(doc2Item);
        const doc1UnitPrice = this.formatPriceForTSV(doc1Item);
        const doc2UnitPrice = this.formatPriceForTSV(doc2Item);
        const doc1Amount = doc1Item.ocrAmountPerItem || '';
        const doc2Amount = doc2Item.ocrAmountPerItem || '';

        // 差分をチェック
        const differences = [];
        if (doc1GoodsShortName !== doc2GoodsShortName)
          differences.push('商品名');
        if (doc1GoodsRefNo1 !== doc2GoodsRefNo1) differences.push('商品番号');
        if (doc1Quantity !== doc2Quantity) differences.push('数量');
        if (doc1UnitPrice !== doc2UnitPrice) differences.push('単価');
        if (doc1Amount !== doc2Amount) differences.push('金額');

        const isMatch = differences.length === 0;
        const judgment = isMatch ? 'OK' : 'NG';
        const diffText =
          differences.length > 0 ? differences.join(', ') : '-';

        rows.push([
          lineNo,
          doc1Items[i] ? doc1GoodsShortName || '( no result )' : '( no result )',
          doc1Items[i] ? doc1GoodsRefNo1 || '( no result )' : '( no result )',
          doc1Items[i] ? doc1Quantity : '( no result )',
          doc1Items[i] ? doc1UnitPrice : '( no result )',
          doc1Items[i] ? doc1Amount || '( no result )' : '( no result )',
          doc2Items[i] ? doc2GoodsShortName || '( no result )' : '( no result )',
          doc2Items[i] ? doc2GoodsRefNo1 || '( no result )' : '( no result )',
          doc2Items[i] ? doc2Quantity : '( no result )',
          doc2Items[i] ? doc2UnitPrice : '( no result )',
          doc2Items[i] ? doc2Amount || '( no result )' : '( no result )',
          judgment,
          diffText,
        ]);
      }

      this.generateTSVFile(headers, rows, '明細項目比較結果');
    },
    // 状態ラベルの取得
    getStatusLabel(status) {
      const statusLabels = {
        match: '一致',
        doc1Only: '書類1のみ',
        doc2Only: '書類2のみ',
        duplicate: '重複',
      };
      return statusLabels[status] || '';
    },
    // フィルタ済みデータのTSVダウンロード
    downloadFilteredDetailTSV(filteredData) {
      if (!filteredData || filteredData.length === 0) {
        this.$message.warning('ダウンロードするデータがありません');
        return;
      }

      // TSVヘッダー行
      const headers = [
        '明細No',
        `${this.doc1Label}_商品名`,
        `${this.doc1Label}_商品番号`,
        `${this.doc1Label}_数量`,
        `${this.doc1Label}_単価`,
        `${this.doc1Label}_金額`,
        `${this.doc2Label}_商品名`,
        `${this.doc2Label}_商品番号`,
        `${this.doc2Label}_数量`,
        `${this.doc2Label}_単価`,
        `${this.doc2Label}_金額`,
        '判定',
        '差分',
      ];

      // TSVデータ行
      const rows = filteredData.map((row) => [
        row.lineNo || '',
        row.doc1_goodsShortName || '( no result )',
        row.doc1_goodsRefNo1 || '( no result )',
        row.doc1_quantity1 || '( no result )',
        row.doc1_unitPrice || '( no result )',
        row.doc1_amount || '( no result )',
        row.doc2_goodsShortName || '( no result )',
        row.doc2_goodsRefNo1 || '( no result )',
        row.doc2_quantity1 || '( no result )',
        row.doc2_unitPrice || '( no result )',
        row.doc2_amount || '( no result )',
        row.judgment || '',
        row.differences || '',
      ]);

      this.generateTSVFile(headers, rows, '明細項目比較結果');
    },
    // TSVファイル生成・ダウンロード
    generateTSVFile(headers, rows, filename) {
      // TSV形式に変換（タブ区切り）
      const tsvContent = [headers, ...rows]
        .map((row) => row.join('\t'))
        .join('\n');

      // BOM付きUTF-8でBlobを作成（Excelで文字化けしないように）
      const bom = '\uFEFF';
      const blob = new Blob([bom + tsvContent], {
        type: 'text/tab-separated-values;charset=utf-8;',
      });

      // ダウンロード実行
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[-:]/g, '')
        .replace('T', '_');
      const fullFilename = `${filename}_${timestamp}.tsv`;

      link.href = url;
      link.download = fullFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$message.success('TSVファイルをダウンロードしました');
    },
    // TSV用の数量フォーマット
    formatQuantityForTSV(item) {
      if (!item || !item.ocrQuantity1) return '( no result )';
      const unit = item.ocrUnit || '';
      return `${item.ocrQuantity1}${unit}`;
    },
    // TSV用の単価フォーマット
    formatPriceForTSV(item) {
      if (!item || !item.ocrUnitPrice) return '( no result )';
      const currency = item.ocrImCustomsCurrencies || '';
      return `${item.ocrUnitPrice}${currency}`;
    },
    compareHeaderItems() {
      // 新しいデータ構造から書類を取得（書類1と書類2）
      const doc1 = this.compareData?.documents?.[0];
      const doc2 = this.compareData?.documents?.[1];

      if (!doc1 || !doc2) {
        console.error('必要な書類データが見つかりません');
        return;
      }

      // 各書類が INV かどうかを判定
      const doc1IsInv =
        this.selectedDocuments?.[0]?.documentCategory === 'INV';
      const doc2IsInv =
        this.selectedDocuments?.[1]?.documentCategory === 'INV';

      const doc1Items = doc1.ocrResults.aiocrItems;
      const doc2Items = doc2.ocrResults.aiocrItems;

      // 書類1のtwFieldNameをキーにしたマップを作成
      const doc1Map = {};
      doc1Items.forEach((item) => {
        doc1Map[item.twFieldName] = item;
      });

      // 書類2のtwFieldNameをキーにしたマップを作成
      const doc2Map = {};
      doc2Items.forEach((item) => {
        doc2Map[item.twFieldName] = item;
      });

      // 比較結果を作成（displayHeaderFields の順序で処理）
      const comparisonResults = [];
      this.displayHeaderFields.forEach((fieldName) => {
        // exCustomsTotalQuantityUnit は単独では表示しない（exCustomsTotalQuantity に結合される）
        if (fieldName === 'exCustomsTotalQuantityUnit') {
          return;
        }

        // 仮想フィールド: 明細行数
        if (fieldName === 'detailRowCount') {
          const doc1RowCount =
            doc1.ocrResults?.aiocrTableItems?.goodsLine?.length || 0;
          const doc2RowCount =
            doc2.ocrResults?.aiocrTableItems?.goodsLine?.length || 0;

          const doc1Value = String(doc1RowCount);
          const doc2Value = String(doc2RowCount);
          const isMatch = doc1RowCount === doc2RowCount;

          comparisonResults.push({
            id: comparisonResults.length + 1,
            twFieldName: fieldName,
            ocrFieldName: this.displayFieldLabels[fieldName] || fieldName,
            doc1Value,
            doc2Value,
            matchStatus: isMatch ? 'OK' : 'NG',
            isMatch,
            differences: this.calculateHeaderDifferences(
              fieldName,
              this.displayFieldLabels[fieldName] || fieldName,
              doc1Value,
              doc2Value,
              isMatch,
              doc1,
              doc2,
            ),
          });

          return; // 仮想フィールドの処理完了
        }

        // contractNo の場合、書類タイプに応じてフィールド名を切り替え
        let doc1FieldName = fieldName;
        let doc2FieldName = fieldName;

        if (fieldName === 'contractNo') {
          doc1FieldName = doc1IsInv ? 'sellersRefNo' : 'contractNo';
          doc2FieldName = doc2IsInv ? 'sellersRefNo' : 'contractNo';
        }

        // exCustoms フィールドが見つからない場合 imCustoms をフォールバック
        const imAlias = {
          exCustomsTotalAmount: 'imCustomsTotalAmount',
          exCustomsTotalQuantity: 'imCustomsTotalQuantity',
        };
        const doc1Item =
          doc1Map[doc1FieldName] || doc1Map[imAlias[doc1FieldName]];
        const doc2Item =
          doc2Map[doc2FieldName] || doc2Map[imAlias[doc2FieldName]];

        let doc1Value = doc1Item?.ocrFieldValue || '';
        let doc2Value = doc2Item?.ocrFieldValue || '';

        // 合計数量の場合は、数量と単位を結合して表示
        if (fieldName === 'exCustomsTotalQuantity') {
          const doc1Quantity = doc1Item?.ocrFieldValue || '';
          const doc1Unit = (
            doc1Map['exCustomsTotalQuantityUnit'] ||
            doc1Map['imCustomsTotalQuantityUnit']
          )?.ocrFieldValue || '';
          doc1Value = doc1Quantity
            ? `${doc1Quantity} ${doc1Unit}`.trim()
            : '';

          const doc2Quantity = doc2Item?.ocrFieldValue || '';
          const doc2Unit = (
            doc2Map['exCustomsTotalQuantityUnit'] ||
            doc2Map['imCustomsTotalQuantityUnit']
          )?.ocrFieldValue || '';
          doc2Value = doc2Quantity
            ? `${doc2Quantity} ${doc2Unit}`.trim()
            : '';
        }

        // 合計金額の場合は通貨単位を隣に表示
        // 通貨が異なる場合は比較をスキップ
        let currencyMismatch = false;
        if (fieldName === 'exCustomsTotalAmount') {
          const doc1Currency = (
            doc1Map['exCustomsCurrency'] ||
            doc1Map['imCustomsCurrency']
          )?.ocrFieldValue || '';
          const doc2Currency = (
            doc2Map['exCustomsCurrency'] ||
            doc2Map['imCustomsCurrency']
          )?.ocrFieldValue || '';
          currencyMismatch = doc1Currency !== doc2Currency;
          doc1Value = doc1Value
            ? `${doc1Value}${doc1Currency ? ' ' + doc1Currency : ''}`
            : '';
          doc2Value = doc2Value
            ? `${doc2Value}${doc2Currency ? ' ' + doc2Currency : ''}`
            : '';
        }

        // 値を比較（空文字列も考慮）
        // 両方とも空の場合はOK
        const bothEmpty = !doc1Value && !doc2Value;
        const bothExistAndMatch =
          doc1Item && doc2Item && doc1Value === doc2Value;
        const isMatch = currencyMismatch
          ? false
          : bothEmpty || bothExistAndMatch;

        // 差分を計算（通貨不一致時は固定メッセージ）
        const differences = currencyMismatch
          ? '単位通貨が異なるため、チェックしません'
          : this.calculateHeaderDifferences(
              fieldName,
              this.displayFieldLabels[fieldName] || fieldName,
              doc1Value,
              doc2Value,
              isMatch,
              doc1,
              doc2,
            );

        comparisonResults.push({
          id: comparisonResults.length + 1,
          twFieldName: fieldName,
          ocrFieldName: this.displayFieldLabels[fieldName] || fieldName,
          doc1Value: doc1Value || '( no result )',
          doc2Value: doc2Value || '( no result )',
          matchStatus: isMatch ? 'OK' : 'NG',
          isMatch,
          differences,
        });
      });

      this.comparisonData = comparisonResults;
      // console.log('ヘッダー項目比較結果:', comparisonResults);
    },

    /**
     * ヘッダー項目の差分を計算
     * @param {String} twFieldName - フィールド名（内部名）
     * @param {String} ocrFieldName - フィールド名（表示用）
     * @param {String} doc1Value - 書類1の値
     * @param {String} doc2Value - 書類2の値
     * @param {Boolean} isMatch - 一致しているか
     * @param {Object} doc1 - 書類1のドキュメントオブジェクト
     * @param {Object} doc2 - 書類2のドキュメントオブジェクト
     * @returns {String} - 差分テキスト
     */
    calculateHeaderDifferences(
      twFieldName,
      ocrFieldName,
      doc1Value,
      doc2Value,
      isMatch,
      doc1,
      doc2,
    ) {
      // 一致している場合
      if (isMatch) {
        return '一致しています';
      }

      // ファイル名を取得
      const doc1Name = doc1?.metadata?.documentName || '書類1';
      const doc2Name = doc2?.metadata?.documentName || '書類2';

      // 空値は ( no result ) と表示
      const doc1Display = doc1Value || '( no result )';
      const doc2Display = doc2Value || '( no result )';

      // 差分がある場合
      let diffText = `${ocrFieldName}に差異があります\n・${doc1Name}：${doc1Display}\n・${doc2Name}：${doc2Display}`;

      // TotalAmount の場合は金額差分と割合を追加
      if (twFieldName === 'totalAmount' || twFieldName.includes('Amount')) {
        const amountDiff = this.calculateAmountDifference(doc1Value, doc2Value);
        if (amountDiff) {
          diffText += `\n${amountDiff}`;
        }
      }

      return diffText;
    },

    /**
     * 金額差分を計算
     * @param {String} value1 - 値1（例: "100000USD"）
     * @param {String} value2 - 値2（例: "105000USD"）
     * @returns {String|null} - 差分テキスト
     */
    calculateAmountDifference(value1, value2) {
      const num1 = parseFloat(String(value1).replace(/,/g, ''));
      const num2 = parseFloat(String(value2).replace(/,/g, ''));

      // 数値として解釈できない場合（例: "USD 23200.00" のような形式）はスキップ
      if (Number.isNaN(num1) || Number.isNaN(num2) || num1 === 0) {
        return null;
      }

      const diffAmount = num2 - num1;
      const diffPercent = ((diffAmount / num1) * 100).toFixed(1);

      return `・差分：${diffAmount.toLocaleString()}（${diffPercent}%）`;
    },
  },
};
</script>

<style lang="scss" scoped>
.drawer-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  padding-bottom: 48px;
}

.drawer-header {
  flex-shrink: 0;
  padding: 20px 24px;
  border-bottom: 1px solid #e4e7ed;
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  min-height: 0;
}

.content-area {
  width: 100%;
  padding: 20px;
}

.selected-documents-info {
  .document-collapse {
    border: 1px solid #e4e7ed;
    border-radius: 4px;

    // アコーディオンの矢印を下向きに変更
    ::v-deep .el-collapse-item__arrow {
      transform: rotate(90deg); // 閉じているときは下向き
    }

    // 展開時も下向きのまま（回転させない）
    ::v-deep .el-collapse-item__arrow.is-active {
      transform: rotate(90deg); // 開いているときも下向き
    }

    .collapse-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding-right: 10px;

      .document-title {
        display: flex;
        align-items: center;
        flex: 1;

        .document-name {
          color: #606266;
          font-size: 14px;
        }
      }

      .process-name {
        color: #909399;
        font-size: 14px;
        margin-left: auto;
        padding-right: 10px;
      }
    }

    .collapse-body {
      padding: 16px 20px;
      background-color: #f5f7fa;
      border-top: 1px solid #ebeef5;

      .info-row {
        display: flex;
        margin-bottom: 8px;
        font-size: 14px;

        .label {
          font-weight: 600;
          color: #606266;
          min-width: 100px;
        }

        .value {
          color: #303133;
          flex: 1;
          word-break: break-all;
        }
      }

      .pdf-preview-container {
        margin-top: 20px;
        border-top: 1px solid #ebeef5;
        padding-top: 20px;
      }

      .pdf-preview-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
      }

      .pdf-preview-header {
        width: 100%;
        max-width: 800px;
        display: flex;
        justify-content: flex-end;
        margin-bottom: 8px;
      }

      .pdf-preview-iframe {
        width: 100%;
        max-width: 800px;
        height: 350px;
        border: 1px solid #dcdfe6;
        border-radius: 4px;
      }

      .pdf-loading {
        text-align: center;
        padding: 40px;
        color: #909399;
        font-size: 14px;
      }

      .pdf-placeholder {
        text-align: center;
        padding: 40px;
      }
    }
  }
}

.comparison-result {
  margin-top: 20px;

  .comparison-result-header {
    display: flex;
    align-items: baseline;
    margin-bottom: 16px;
  }

  ::v-deep .el-tabs__header {
    margin-bottom: 15px;
  }

  ::v-deep .el-tabs__content {
    overflow: visible;
  }

  .text-error {
    color: #f56c6c;
  }
}
</style>

<style lang="scss">
.custom-success-alert {
  border: 2px solid #67c23a !important;
  border-radius: 4px;

  .el-alert__content {
    color: #999 !important;
    font-weight: 500;
    line-height: 1.6;
  }
}
</style>
